import { formatVwDate } from '../../../@commons/pipes/vw-date.pipe';
import { DAY_MESSAGES, DayCandidate, DayMessageDef } from './day-message.catalogue';
import { PLANT_RULES, willyQuestion } from './day-message.plant-rules';
import {
  CALM_FAMILIES,
  DayChoice,
  DayContext,
  DayHistoryEntry,
  DayMessage,
  DayPlant,
  DayText,
  isWeatherFresh,
  localDay,
  plantWords,
} from './day-message.model';

/** Tout le catalogue : les cartes du serveur, le Feu vert et les alertes, puis les plantes et les sachets du jardin. */
export const DAY_CATALOGUE: readonly DayMessageDef[] = [...DAY_MESSAGES, ...PLANT_RULES];

/** Autres gestes du jour, sous le message principal. */
export const MAX_OTHERS = 2;
/** À partir de trois plantes, le titre compte au lieu de nommer. */
const MANY_PLANTS = 3;
/** Un geste sans réponse revient trois jours de suite au plus, puis passe dans la quinzaine. */
const MAX_DAYS_IN_A_ROW = 3;
/** Un message calme revient au plus une fois par semaine. */
const CALM_GAP_DAYS = 7;
/** Le seul message calme permis sans météo à jour : il part de l'état des plantes. */
const CALM_WITHOUT_WEATHER = 'RIEN_DE_SPECIAL';
/** Ce qu'on ne sait pas sans météo n'est dit qu'en saison de gel. */
const FROST_SEASON = [10, 11, 12, 1, 2, 3, 4, 5];

/**
 * Le mot du jour (règle 1 de la proposition A) : un message principal, deux
 * autres gestes au plus, le reste pour « Cette quinzaine ». Fonction pure : ce
 * que l'accueil a chargé, l'historique de cet appareil, le catalogue.
 *
 * 1. Chaque règle du catalogue trouve ses candidats ; ce qui a déjà reçu une
 *    réponse aujourd'hui en sort, y compris une réponse gardée sans réseau,
 *    et une plante nommée par une règle plus précise (`overrides`) n'est
 *    plus nommée par la règle générale.
 * 2. Un même geste sur plusieurs plantes devient un seul message.
 * 3. Sans météo à jour, rien de ce qui se fonde sur la prévision, et le seul
 *    message calme est celui qui part des plantes (le hors-saison et les
 *    premiers pas ne disent rien du temps : ils restent).
 * 4. Les priorités 6 et 7 n'existent qu'un jour sans geste de 1 à 5.
 * 5. Ordre : priorité, rang dans la sécurité (le gel devant le vent), la
 *    plante la plus jeune, l'événement le plus proche, puis ce qui n'était pas
 *    en tête hier.
 * 6. Un geste resté sans réponse trois jours de suite passe dans la quinzaine
 *    (jamais la sécurité ni une perte à quelques jours) ;
 *    un message calme ne revient ni le lendemain ni dans la semaine.
 * 7. Une plante n'apparaît qu'une fois sur l'écran, et un message qui parle de
 *    tout le jardin (« Tes 2 plantes font une pause ») ne côtoie pas un message
 *    qui en nomme une (« Tes poireaux peuvent rester en terre ») : le second
 *    venu passe dans la quinzaine.
 */
export function chooseDayMessages(
  ctx: DayContext,
  history: readonly DayHistoryEntry[] = [],
  defs: readonly DayMessageDef[] = DAY_CATALOGUE,
  /** Les messages « une fois » déjà montrés sur cet appareil. */
  shownOnce: readonly string[] = [],
): DayChoice {
  const weatherFresh = isWeatherFresh(ctx.advice, ctx.now);
  const today = localDay(ctx.now);
  const month = ctx.now.getMonth() + 1;
  const answered = answeredToday(ctx, today);
  const past = history.filter((entry) => entry.day < today);
  const yesterdayHead = past.filter((e) => e.day === shiftDay(today, -1) && e.head)[0] ?? null;

  const eligible = (def: DayMessageDef) =>
    !(def.months && !def.months.includes(month)) &&
    !(def.once && shownOnce.includes(def.id)) &&
    !(def.weather && !weatherFresh) &&
    !(!weatherFresh && def.family === 'calme' && def.id !== CALM_WITHOUT_WEATHER);
  // Ce qui a été montré hier : « Pas besoin d'arroser » clôt la soif d'hier.
  const withHistory = { ...ctx, recentCodes: ctx.recentCodes ?? past.filter((e) => e.day === shiftDay(today, -1)).map((e) => e.code) };
  const found = defs.map((def) => (eligible(def) ? def.collect(withHistory) : []));
  const claimed = claimedPlants(defs, found);

  const messages = defs.flatMap((def, order) => {
    // Ce qui a reçu sa réponse part, comme ce qu'une règle plus précise nomme ; un candidat dont toutes les plantes sont parties aussi.
    const candidates = found[order].flatMap((c) => {
      if (answered.has(answerKey(c.code, null))) return [];
      const plants = c.plants.filter((p) => !answered.has(answerKey(c.code, p.uid)) && !claimed.get(def.id)?.has(plantKey(p)));
      return c.plants.length && !plants.length ? [] : [{ ...c, plants }];
    });
    return group(def, candidates).map((c) => toMessage(def, c, ctx, order));
  });

  const actions = messages.filter((m) => m.priority <= 5);
  let pool: DayMessage[];
  let rest: DayMessage[] = [];
  if (actions.length) {
    const sorted = sortMessages(actions, yesterdayHead);
    // La sécurité et les pertes à quelques jours ne se lassent jamais : elles restent tant que le risque tient.
    const tired = sorted.filter(
      (m) => m.family !== 'securite' && m.priority > 2 && shownDaysInARow(past, m.key, today) >= MAX_DAYS_IN_A_ROW,
    );
    pool = sorted.filter((m) => !tired.includes(m));
    rest = tired;
    // Tout est las : le plus urgent reste en tête plutôt qu'un accueil vide.
    if (!pool.length) pool = rest.splice(0, 1);
  } else {
    pool = rotateCalm(sortMessages(messages, yesterdayHead), past, today);
  }

  const { kept, moved } = onePlantOnce(pool);
  pool = kept;
  rest = [...rest, ...moved];
  const head = pool[0] ?? null;
  if (head && !weatherFresh && FROST_SEASON.includes(month)) {
    head.note = { key: 'home.day.no-weather' };
  }
  // Un jour calme montre ce qu'on peut demander à Willy.
  const willy = head && CALM_FAMILIES.includes(head.family) ? willyQuestion(ctx) : null;
  return { head, others: pool.slice(1, 1 + MAX_OTHERS), rest: [...pool.slice(1 + MAX_OTHERS), ...rest], weatherFresh, willy };
}

/** Ce qui a reçu une réponse aujourd'hui : code et plante, ou code seul pour un message général. */
function answeredToday(ctx: DayContext, today: string): Set<string> {
  const answers = [...(ctx.advice?.responses ?? []), ...(ctx.pendingReplies ?? [])];
  return new Set(answers.filter((r) => r.day === today).map((r) => answerKey(r.triggerCode, r.plantUid ?? null)));
}

function answerKey(code: string, plantUid: string | null): string {
  return `${code}|${plantUid ?? ''}`;
}

function plantKey(plant: DayPlant): string {
  return plant.uid ?? `name:${plant.name}`;
}

/** Les plantes que chaque règle générale laisse aux règles plus précises, par identifiant de règle. */
function claimedPlants(defs: readonly DayMessageDef[], found: DayCandidate[][]): Map<string, Set<string>> {
  const claimed = new Map<string, Set<string>>();
  defs.forEach((def, i) => {
    for (const id of def.overrides ?? []) {
      const plants = claimed.get(id) ?? new Set<string>();
      found[i].forEach((c) => c.plants.forEach((p) => plants.add(plantKey(p))));
      claimed.set(id, plants);
    }
  });
  return claimed;
}

/**
 * Un même geste sur plusieurs plantes : un seul candidat, les plantes à la
 * suite. Une règle dont le geste change d'une culture à l'autre
 * (`groupBy: 'cultivation'`) groupe par culture : les poireaux en tête, le
 * chou frisé en ligne.
 */
function group(def: DayMessageDef, candidates: DayCandidate[]): DayCandidate[] {
  if (!def.group || candidates.length < 2) return candidates;
  if (def.groupBy === 'cultivation') {
    const byCrop = new Map<string, DayCandidate[]>();
    for (const c of candidates) {
      const crop = c.plants[0]?.cultivationUid ?? c.plants[0]?.name ?? '';
      byCrop.set(crop, [...(byCrop.get(crop) ?? []), c]);
    }
    return [...byCrop.values()].map(merge);
  }
  return [merge(candidates)];
}

function merge(candidates: DayCandidate[]): DayCandidate {
  const [first] = candidates;
  if (candidates.length === 1) return first;
  const plants = dedupe(candidates.flatMap((c) => c.plants));
  const dates = candidates.map((c) => c.eventDate).filter((d): d is string => !!d).sort();
  return { ...first, plants, estimated: candidates.some((c) => c.estimated), eventDate: dates[0] ?? first.eventDate ?? null };
}

/**
 * Une plante une seule fois sur l'écran, dans l'ordre de lecture : un message
 * qui en reprend une déjà nommée passe dans la quinzaine. Un message de tout
 * le jardin (`wholeGarden`) les nomme toutes à la fois.
 */
function onePlantOnce(pool: DayMessage[]): { kept: DayMessage[]; moved: DayMessage[] } {
  const named = new Set<string>();
  let wholeGarden = false;
  const kept: DayMessage[] = [];
  const moved: DayMessage[] = [];
  for (const m of pool) {
    const keys = m.plants.map(plantKey);
    const clash = m.wholeGarden ? wholeGarden || named.size > 0 : keys.some((k) => named.has(k)) || (wholeGarden && keys.length > 0);
    if (clash) {
      moved.push(m);
      continue;
    }
    kept.push(m);
    keys.forEach((k) => named.add(k));
    wholeGarden ||= !!m.wholeGarden;
  }
  return { kept, moved };
}

function dedupe(plants: DayPlant[]): DayPlant[] {
  const seen = new Set<string>();
  return plants.filter((p) => {
    const key = plantKey(p);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function toMessage(def: DayMessageDef, c: DayCandidate, ctx: DayContext, order: number): DayMessage {
  const base = `home.day.msg.${def.id}`;
  const look = def.like ?? def.id;
  const many = def.group && c.plants.length >= MANY_PLANTS;
  const n = c.params?.['n'];
  const one = def.counted && n === 1 ? '_one' : '';
  const words = plantWords(c.plants, ctx.lang, def.number);
  const form = many ? null : words.form;
  const params = { ...c.params, ...words.params, count: many ? c.plants.length : (n ?? c.plants.length) };
  const title = many ? 'title-many' : c.plants.length && !words.agreed ? 'title-plain' : `title${one}`;
  const titleText: DayText = c.titleText ? { text: c.titleText } : { key: `${base}.${title}`, params, form };
  const reason = [
    // Trois plantes et plus : le titre les compte, la raison les nomme.
    ...(many ? [{ key: 'home.day.reason.names', params }] : []),
    ...(c.reason ?? []),
    ...(def.defaultReason ? [{ key: `${base}.reason${one}`, params, form }] : []),
  ];
  return {
    key: `${def.id}:${c.code}:${c.plants.map((p) => p.uid ?? p.name).join(',')}`,
    code: c.code,
    family: c.family ?? def.family,
    priority: c.priority ?? def.priority,
    tone: c.tone ?? def.tone,
    plants: c.plants,
    signal: { icon: def.icon, key: `home.day.signal.${look}${c.signalVariant ? '.' + c.signalVariant : ''}`, params },
    announces: c.announces !== undefined ? c.announces : (def.announces ?? null),
    source: def.weather && ctx.advice?.forecastAt ? forecastSource(ctx.advice.forecastAt, ctx) : null,
    title: titleText,
    // L'impératif court n'existe que sur un titre accordé : sans accord, la ligne garde le titre sans accord.
    rowTitle: !c.titleText && words.agreed && !many ? { key: `${base}.row-title`, params, form, fallback: titleText } : titleText,
    shortTitle: { key: `${base}.short${one}`, params, form },
    noted: words.agreed && def.button?.action === 'DONE' ? notedText(def.button.key, words) : null,
    reason,
    tag: { key: `home.day.tag.${look}` },
    rowReason: def.rowReason
      ? { key: `${base}.row`, params }
      : c.plants[0]?.location
        ? { text: c.plants[0].location }
        : null,
    estimated: !!(c.estimated ?? def.estimated),
    note: null,
    button: def.button ?? null,
    link: c.link ?? null,
    eventDate: c.eventDate ?? null,
    alertUid: c.alertUid,
    rank: (def.rank ?? 100) * 1000 + order,
    once: def.once,
    wholeGarden: def.wholeGarden,
  };
}

/**
 * La confirmation après « C'est fait », accordée au geste et aux plantes
 * (« Pommes de terre couvertes, c'est noté. »). Un geste sans participe
 * (« C'est fait ») n'a pas de clé : l'accueil dit alors « Pomme de terre,
 * c'est noté. ».
 */
function notedText(gesture: string, words: ReturnType<typeof plantWords>): DayText {
  return { key: `home.day.noted-as.${gesture}`, params: { bare: words.params['bare'] }, form: words.form };
}

/** « prévu à 6 h », ou « prévu dimanche à 6 h » quand la prévision n'est pas du jour. */
function forecastSource(at: string, ctx: DayContext): DayText {
  const time = formatVwDate(at, 'hour', ctx.lang);
  return localDay(new Date(at)) === localDay(ctx.now)
    ? { key: 'home.day.source.forecast-at', params: { time } }
    : { key: 'home.day.source.forecast-at-day', params: { time, day: formatVwDate(at, 'weekday', ctx.lang) } };
}

/** Priorité, rang, plante la plus jeune, événement le plus proche, puis ce qui n'était pas en tête hier. */
function sortMessages(messages: DayMessage[], yesterdayHead: DayHistoryEntry | null): DayMessage[] {
  const youngest = (m: DayMessage) => m.plants.map((p) => p.plantedAt ?? '').sort().reverse()[0] ?? '';
  return [...messages].sort(
    (a, b) =>
      a.priority - b.priority ||
      (a.family === 'securite' && b.family === 'securite' ? a.rank - b.rank : 0) ||
      youngest(b).localeCompare(youngest(a)) ||
      (a.eventDate ?? '9999').localeCompare(b.eventDate ?? '9999') ||
      Number(a.key === yesterdayHead?.key) - Number(b.key === yesterdayHead?.key) ||
      a.rank - b.rank,
  );
}

/**
 * Les jours calmes tournent : jamais le message ni la plante d'hier, chaque
 * code calme une fois par semaine au plus, d'abord ce qui part d'une plante,
 * puis le jardin et les graines, les premiers pas (alertes, l'app) en dernier,
 * et le moins récemment montré. Le repli reste possible s'il ne reste rien.
 */
function rotateCalm(messages: DayMessage[], past: DayHistoryEntry[], today: string): DayMessage[] {
  const calm = messages.filter((m) => CALM_FAMILIES.includes(m.family));
  if (!calm.length) return messages;
  const yesterday = past.filter((e) => e.day === shiftDay(today, -1));
  const lastShown = (code: string) => past.filter((e) => e.code === code).map((e) => e.day).sort().reverse()[0] ?? '';
  const weekAgo = shiftDay(today, -CALM_GAP_DAYS);
  const fresh = calm.filter((m) => {
    const sameAsYesterday = yesterday.some(
      (e) => e.code === m.code || m.plants.some((p) => p.uid && e.plants.includes(p.uid)),
    );
    return !sameAsYesterday && lastShown(m.code) <= weekAgo;
  });
  const order = (list: DayMessage[]) =>
    [...list].sort(
      (a, b) =>
        Number(a.code === CALM_WITHOUT_WEATHER) - Number(b.code === CALM_WITHOUT_WEATHER) ||
        Number(b.plants.some((p) => !!p.uid)) - Number(a.plants.some((p) => !!p.uid)) ||
        Number(a.family === 'premier_pas') - Number(b.family === 'premier_pas') ||
        lastShown(a.code).localeCompare(lastShown(b.code)),
    );
  const chosen = fresh.length ? order(fresh) : order(calm).slice(0, 1);
  const others = messages.filter((m) => !CALM_FAMILIES.includes(m.family));
  // Une information de croissance (priorité 6) passe devant un jour calme.
  return [...others, ...chosen];
}

/** Jours de suite (jusqu'à hier) où ce message a été montré. */
function shownDaysInARow(past: DayHistoryEntry[], key: string, today: string): number {
  let count = 0;
  for (let day = shiftDay(today, -1); past.some((e) => e.day === day && e.key === key); day = shiftDay(day, -1)) count++;
  return count;
}

function shiftDay(day: string, delta: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return localDay(new Date(y, m - 1, d + delta));
}
